import React, { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import AIButton from "@/components/AIButton";
import MicButton from "@/components/MicButton";
import { getTool, serializeTool, DELIVERABLE_DOCS } from "@/data/activityTools";
import { AI_ACTIONS, isIntakeComplete } from "@/data/bridgeData";
import { useEntries } from "@/context/EntriesContext";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Plus, Trash2, ArrowRight, Check, FileText } from "lucide-react";

function ListEditor({ tool, readOnly }) {
  const { getEntry, saveEntry } = useEntries();
  const key = `tool:${tool.id}`;
  const items = getEntry(key, []);
  const list = Array.isArray(items) ? items : [];

  const update = (idx, fieldId, value) => {
    const next = list.map((it, i) => (i === idx ? { ...it, [fieldId]: value } : it));
    saveEntry(key, next);
  };
  const add = () => saveEntry(key, [...list, { _id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }], true);
  const remove = (idx) => saveEntry(key, list.filter((_, i) => i !== idx), true);

  return (
    <div className="space-y-4">
      {list.length === 0 && (
        <p className="text-sm text-stone-400">No entries yet – add your first {tool.itemLabel?.toLowerCase()}.</p>
      )}
      {list.map((item, idx) => (
        <div key={item._id || idx} className="rounded-sm border border-stone-200 bg-white p-6">
          <div className="mb-3 flex items-center justify-between">
            <span className="label-eyebrow">{tool.itemLabel} {idx + 1}</span>
            {!readOnly && (
              <button data-testid={`tool-remove-${idx}`} onClick={() => remove(idx)} className="text-stone-300 hover:text-[#9E4A41] transition-colors">
                <Trash2 size={16} />
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6">
            {tool.fields.map((f) => (
              <div key={f.id} className={`mb-4 ${f.type === "textarea" ? "sm:col-span-2" : ""}`}>
                <label className="text-sm text-stone-500">{f.label}</label>
                {f.type === "textarea" ? (
                  <>
                    <textarea
                      data-testid={`tool-${tool.id}-${idx}-${f.id}`}
                      readOnly={readOnly}
                      value={item?.[f.id] || ""}
                      onChange={(e) => update(idx, f.id, e.target.value)}
                      rows={2}
                      placeholder={f.placeholder || ""}
                      className="mt-1 w-full resize-y bg-transparent border-b border-stone-300 py-1.5 text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
                    />
                    {!readOnly && (
                      <div className="mt-1 flex justify-end">
                        <MicButton getValue={() => item?.[f.id] || ""} onChange={(v) => update(idx, f.id, v)} testid={`tool-${tool.id}-${idx}-${f.id}-mic`} />
                      </div>
                    )}
                  </>
                ) : (
                  <input
                    data-testid={`tool-${tool.id}-${idx}-${f.id}`}
                    readOnly={readOnly}
                    value={item?.[f.id] || ""}
                    onChange={(e) => update(idx, f.id, e.target.value)}
                    placeholder={f.placeholder || ""}
                    className="mt-1 w-full bg-transparent border-b border-stone-300 py-1.5 text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {!readOnly && (!tool.max || list.length < tool.max) && (
        <button
          data-testid="tool-add-item"
          onClick={add}
          className="inline-flex items-center gap-2 rounded-sm border border-dashed border-stone-300 px-4 py-2.5 text-sm text-stone-600 hover:border-brand-primary hover:text-brand-primary transition-colors"
        >
          <Plus size={15} /> Add {tool.itemLabel?.toLowerCase()}
          {tool.min ? <span className="text-stone-400">· aim for {tool.min}{tool.max ? `–${tool.max}` : "+"}</span> : null}
        </button>
      )}
    </div>
  );
}

function WritingEditor({ tool, readOnly }) {
  const { getEntry, saveEntry } = useEntries();
  return (
    <div className="space-y-7">
      {tool.fields.map((f) => {
        const key = `tool:${tool.id}:${f.id}`;
        return (
          <div key={f.id}>
            <label className="font-serif text-xl text-stone-900">{f.label}</label>
            {f.prompts?.map((p, i) => <p key={i} className="text-sm italic text-stone-500">– {p}</p>)}
            <textarea
              data-testid={`tool-${tool.id}-${f.id}`}
              readOnly={readOnly}
              value={getEntry(key, "")}
              onChange={(e) => saveEntry(key, e.target.value)}
              rows={f.rows || 5}
              placeholder={"Write or speak freely\u2026"}
              className="mt-2 w-full resize-y bg-transparent border-b border-stone-300 py-2 text-base leading-relaxed text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
            />
            {!readOnly && (
              <div className="mt-1.5 flex justify-end">
                <MicButton getValue={() => getEntry(key, "")} onChange={(v) => saveEntry(key, v)} testid={`tool-${tool.id}-${f.id}-mic`} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SkillsEditor({ tool, readOnly }) {
  const { getEntry, saveEntry } = useEntries();
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const f = (id) => `tool:${tool.id}:${id}`;
  const qKey = `tool:${tool.id}:questions`;
  const synthKey = `tool:${tool.id}:synth`;
  const questions = getEntry(qKey, "");
  const synth = getEntry(synthKey, "");

  const ctxBase = () =>
    `Current industry / audience: ${getEntry(f("current_audience"), "")}\n` +
    `Desired audience: ${getEntry(f("desired_audience"), "")}\n\n` +
    `My skills, in my own words:\n${getEntry(f("skills_own_words"), "")}`;
  const ctxFull = () => `${ctxBase()}\n\nClarifying questions:\n${questions}\n\nMy answers:\n${getEntry(f("answers"), "")}`;

  const send = () => {
    if (!synth.trim()) return;
    saveEntry(tool.deliverable, synth, true);
    setSent(true);
    setTimeout(() => setSent(false), 2500);
  };

  const TextInput = ({ id, label, placeholder }) => (
    <div>
      <label className="text-sm text-stone-500">{label}</label>
      <input
        data-testid={`skills-${id}`}
        readOnly={readOnly}
        value={getEntry(f(id), "")}
        onChange={(e) => saveEntry(f(id), e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border-b border-stone-300 py-1.5 text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
      />
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Step 1: audiences */}
      <div className="rounded-sm border border-stone-200 bg-white p-6">
        <div className="label-eyebrow mb-4">Step 1 · From where, to where</div>
        <div className="grid sm:grid-cols-2 gap-6">
          <TextInput id="current_audience" label="Your current industry / audience" placeholder="Who you've served so far" />
          <TextInput id="desired_audience" label="Your desired audience" placeholder="Who you want to serve next" />
        </div>
      </div>

      {/* Step 2: skills in own words */}
      <div className="rounded-sm border border-stone-200 bg-white p-6">
        <div className="label-eyebrow mb-3">Step 2 · Your skills, in your own words</div>
        <p className="text-sm text-stone-500 mb-3">Don't translate yet, just describe what you're good at the way you'd say it to a colleague.</p>
        <textarea
          data-testid="skills-skills_own_words"
          readOnly={readOnly}
          value={getEntry(f("skills_own_words"), "")}
          onChange={(e) => saveEntry(f("skills_own_words"), e.target.value)}
          rows={6}
          placeholder={"Write or speak freely\u2026"}
          className="w-full resize-y bg-transparent border-b border-stone-300 py-2 text-base leading-relaxed text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
        />
        {!readOnly && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <MicButton getValue={() => getEntry(f("skills_own_words"), "")} onChange={(v) => saveEntry(f("skills_own_words"), v)} testid="skills-skills_own_words-mic" />
            <AIButton
              task="skills-questions"
              label="Ask me clarifying questions"
              testid="skills-ai-questions"
              getInput={ctxBase}
              onResult={(out) => saveEntry(qKey, out, true)}
            />
          </div>
        )}
      </div>

      {/* Step 3: questions + answers */}
      {questions && (
        <div className="rounded-sm border border-brand-sage/30 bg-brand-sage/10 p-6">
          <div className="mb-2 flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.6} className="text-brand-sage" />
            <span className="label-eyebrow" style={{ color: "#697A76" }}>Step 3 · Answer these to go deeper</span>
          </div>
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-stone-700 mb-4">{questions}</p>
          <textarea
            data-testid="skills-answers"
            readOnly={readOnly}
            value={getEntry(f("answers"), "")}
            onChange={(e) => saveEntry(f("answers"), e.target.value)}
            rows={6}
            placeholder={"Answer in your own words\u2026"}
            className="w-full resize-y bg-white/70 border border-stone-200 rounded-sm p-3 text-base leading-relaxed text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
          />
          {!readOnly && (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <MicButton getValue={() => getEntry(f("answers"), "")} onChange={(v) => saveEntry(f("answers"), v)} testid="skills-answers-mic" />
              <AIButton
                task="transferable-skills"
                label="Build my transferable skills list"
                testid="skills-ai-build"
                getInput={ctxFull}
                onResult={(out) => saveEntry(synthKey, out, true)}
              />
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {synth && (
        <div className="rounded-sm border border-brand-primary/30 bg-brand-parchment/40 p-6">
          <div className="mb-2 flex items-center gap-2">
            <FileText size={15} strokeWidth={1.6} className="text-brand-primary" />
            <span className="label-eyebrow">Your transferable skills map</span>
          </div>
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-stone-700">{synth}</p>
          {!readOnly && (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                data-testid="skills-send-deliverable"
                onClick={send}
                className="group inline-flex items-center gap-2 rounded-sm bg-brand-primary px-4 py-2.5 text-white transition-all duration-300 hover:bg-brand-hover"
              >
                {sent ? <><Check size={16} /> Added</> : <>Add to Transferable skills map <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></>}
              </button>
              <button onClick={() => navigate("/deliverables")} className="text-sm text-brand-primary hover:underline">Open Deliverables</button>
              <button onClick={() => saveEntry(synthKey, "", true)} className="text-xs text-stone-400 hover:text-stone-700 transition-colors">Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ActivityTool() {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getEntry, saveEntry, loading } = useEntries();
  const tool = getTool(toolId);
  const [sent, setSent] = useState(false);

  if (!tool) return <Layout><p className="text-stone-600">Exercise not found.</p></Layout>;

  if (loading) {
    return <Layout><div className="py-20 text-stone-400 text-sm">Loading\u2026</div></Layout>;
  }

  if (user?.role === "participant" && !isIntakeComplete(getEntry)) {
    return <Navigate to="/intake" replace />;
  }

  const readOnly = user?.role === "coach";
  const synthKey = `tool:${tool.id}:synth`;
  const synth = getEntry(synthKey, "");
  const action = AI_ACTIONS[tool.ai] ? { task: AI_ACTIONS[tool.ai].task } : { task: tool.ai };
  const deliverable = tool.deliverable ? DELIVERABLE_DOCS.find((d) => d.key === tool.deliverable) : null;

  const renderEditor = () => {
    if (tool.kind === "custom") return <SkillsEditor tool={tool} readOnly={readOnly} />;
    if (tool.kind === "list") return <ListEditor tool={tool} readOnly={readOnly} />;
    return <WritingEditor tool={tool} readOnly={readOnly} />;
  };

  const sendToDeliverable = () => {
    const text = synth || serializeTool(getEntry, tool.id);
    if (!text.trim()) return;
    saveEntry(tool.deliverable, text, true);
    setSent(true);
    setTimeout(() => setSent(false), 2500);
  };

  return (
    <Layout coachContext>
      <button onClick={() => navigate(`/sessions/${tool.sessionId}`)} className="text-sm text-stone-500 hover:text-stone-900 transition-colors mb-6">
        ← Back to Session {tool.sessionId}
      </button>

      <div className="mb-8">
        <div className="label-eyebrow mb-3">Session {tool.sessionId} · Exercise</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900">{tool.title}</h1>
        <p className="mt-4 max-w-2xl text-stone-600 leading-relaxed">{tool.intro}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          {renderEditor()}

          {tool.kind !== "custom" && !readOnly && (
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <AIButton
                task={action.task}
                label={tool.aiLabel || "Synthesize"}
                testid={`tool-ai-${tool.id}`}
                getInput={() => serializeTool(getEntry, tool.id)}
                onResult={(out) => saveEntry(synthKey, out, true)}
              />
              {synth && (
                <button onClick={() => saveEntry(synthKey, "", true)} className="text-xs text-stone-400 hover:text-stone-700 transition-colors">
                  Clear synthesis
                </button>
              )}
            </div>
          )}

          {tool.kind !== "custom" && synth && (
            <div className="mt-5 rounded-sm border border-brand-sage/30 bg-brand-sage/10 p-5">
              <div className="mb-2 flex items-center gap-2">
                <Sparkles size={14} strokeWidth={1.6} className="text-brand-sage" />
                <span className="label-eyebrow" style={{ color: "#697A76" }}>Assistant synthesis</span>
              </div>
              <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-stone-700">{synth}</p>
            </div>
          )}
        </div>

        {/* Output → deliverable */}
        <div className="space-y-6">
          {tool.kind !== "custom" && deliverable && (
            <div className="rounded-sm border border-stone-200 bg-brand-parchment/40 p-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText size={15} strokeWidth={1.6} className="text-brand-primary" />
                <div className="label-eyebrow">Use in the program</div>
              </div>
              <p className="text-sm text-stone-600 leading-snug mb-4">
                Send this work to your <span className="text-stone-900 font-medium">{deliverable.title}</span> in the Deliverables library, where you can refine it further.
              </p>
              {!readOnly && (
                <button
                  data-testid="tool-send-deliverable"
                  onClick={sendToDeliverable}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-sm bg-brand-primary px-4 py-2.5 text-white transition-all duration-300 hover:bg-brand-hover"
                >
                  {sent ? <><Check size={16} /> Added</> : <>Add to {deliverable.title} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></>}
                </button>
              )}
              <button onClick={() => navigate("/deliverables")} className="mt-3 w-full text-center text-sm text-brand-primary hover:underline">
                Open Deliverables
              </button>
            </div>
          )}

          <div className="rounded-sm border border-stone-200 bg-white p-6">
            <div className="label-eyebrow mb-3">How this flows</div>
            <ol className="space-y-2 text-sm text-stone-600">
              <li>1 · Do the exercise here.</li>
              <li>2 · Let the assistant synthesise it.</li>
              <li>3 · Send the output into your deliverables.</li>
              <li>4 · Your coach can see it in your studio.</li>
            </ol>
          </div>
        </div>
      </div>
    </Layout>
  );
}
